import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { RiInformation2Line } from "react-icons/ri";

import CoreCard from "../../../ui/core/CoreCard";
import { useObservabilityStore } from "../../../../hooks/useObservabilityStore";
import {
  formatCurrency,
  formatNumber,
  MetricGrid,
  SectionLabel,
  type Metric,
} from "./MetricTiles";
import type { UsageKpis } from "../../../../types/observability";

// PromptWrx cost story: how many cases we handled, how many were billable, and
// the fee split (fixed flat fees + contingency recovery). All chat-grained
// ("case" = chat, REV-1762).
const buildMetrics = (kpis: UsageKpis): Metric[] => [
  { label: "Total Cases", value: formatNumber(kpis.cases) },
  { label: "Billable Cases", value: formatNumber(kpis.billable_cases) },
  { label: "Base Fee", value: formatCurrency(kpis.fixed_cost) },
  { label: "Success Fee", value: formatCurrency(kpis.contingency_cost) },
];

const CostMetricsCard: FC = observer(() => {
  const { isUsageKpisLoading, usageKpis, loadUsageKpis } =
    useObservabilityStore();

  useEffect(() => {
    loadUsageKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CoreCard sx={{ p: 1, minHeight: "auto", height: "auto" }}>
      <SectionLabel
        sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        <span>Cost</span>
        <Tooltip
          title="Includes only transactions that were billed on or before the last billing date."
          arrow
          placement="top"
        >
          <Box component="span" sx={{ display: "inline-flex", cursor: "help" }}>
            <RiInformation2Line size={14} />
          </Box>
        </Tooltip>
      </SectionLabel>
      {isUsageKpisLoading ? (
        <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : !usageKpis ? (
        <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No cost data available
          </Typography>
        </Box>
      ) : (
        <MetricGrid metrics={buildMetrics(usageKpis)} columns={4} />
      )}
    </CoreCard>
  );
});

export default CostMetricsCard;
